import clsx from "clsx";
import type { HeroClass, ItemSlot, PlayerItem } from "../types";
import { getItemIconPath, RARITY_COLOR } from "../utils/assets";
import { AdminAssetPath } from "./AdminAssetPath";

interface EquipCandidatesListProps {
  slot: ItemSlot;
  items: PlayerItem[];
  heroClass: HeroClass;
  heroLevel: number;
  hasEquippedItem: boolean;
  onEquip: (itemId: number) => void;
  actionLoading?: boolean;
}

function itemCanEquip(item: PlayerItem, heroLevel: number): boolean {
  if (item.can_equip !== undefined) return item.can_equip;
  return heroLevel >= item.template.item_level;
}

function canEquipInSlot(item: PlayerItem, slot: ItemSlot, heroClass: HeroClass, heroLevel: number): boolean {
  if (item.is_equipped) return false;
  if (item.template.slot !== slot) return false;
  if (item.template.hero_class && item.template.hero_class !== heroClass) return false;
  return itemCanEquip(item, heroLevel);
}

export function EquipCandidatesList({
  slot,
  items,
  heroClass,
  heroLevel,
  hasEquippedItem,
  onEquip,
  actionLoading,
}: EquipCandidatesListProps) {
  const candidates = items.filter((item) => canEquipInSlot(item, slot, heroClass, heroLevel));

  return (
    <div className="border-t border-kingdom-mid pt-3">
      <div className="text-xs text-gray-400 mb-2">
        {hasEquippedItem ? "Replace with:" : "Equip from inventory:"}
      </div>
      {candidates.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-2">No suitable items in inventory</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {candidates.map((item) => {
            const t = item.template;
            const rarityClass = RARITY_COLOR[t.rarity] ?? RARITY_COLOR.JUNK;
            const iconSrc = getItemIconPath(t.hero_class ?? heroClass, t.slot, t.icon, t.item_level);

            return (
              <button
                key={item.id}
                type="button"
                disabled={actionLoading}
                onClick={() => onEquip(item.id)}
                className={clsx(
                  "w-full flex items-center gap-3 p-2 rounded-xl border bg-kingdom-dark/60 hover:border-kingdom-glow transition-colors text-left disabled:opacity-50",
                  rarityClass.split(" ")[0],
                )}
              >
                <img
                  src={iconSrc}
                  alt={t.name}
                  className={clsx("w-10 h-10 rounded-lg border object-contain bg-kingdom-dark shrink-0", rarityClass)}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{t.name}</div>
                  <div className="text-[10px] text-gray-400">
                    {t.rarity} · Lv.{t.item_level}
                    {item.enhance_level > 0 && ` +${item.enhance_level}`}
                  </div>
                  <AdminAssetPath path={iconSrc} />
                </div>
                <span className="text-kingdom-glow text-xs font-bold shrink-0">Equip</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
